import { ImportFn } from "common/lib/importClient";

/** Maximum number of records submitted in a single import request. */
export const MAX_IMPORT_RECORDS = 2000;

/**
 * One importable field. `key` is the property name the backend importer reads,
 * `label` is what the user maps against in the wizard.
 */
export type ImportFieldDef = {
  key: string;
  label: string;
  required?: boolean;
  hint?: string;
};

export type FieldMapping = Record<string, string>;

export type ImportStatus = "idle" | "running" | "done";

export type ImportResults = {
  total: number;
  successful: number;
  failed: number;
  downloadUrl: string | null;
};

/** Everything the wizard needs to import one kind of record. */
export type DataImportConfig = {
  /** Plural title, e.g. "LTA Services". */
  title: string;
  /** Singular lowercase label used in body copy, e.g. "LTA service". */
  entityLabel: string;
  fields: ImportFieldDef[];
  importFn: ImportFn;
  /** Template file name under `public/templates`. */
  templateFile: string;
  /** Remix icon class for the modal header. */
  icon?: string;
};

function normalizeStr(s: string): string {
  return s
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

function similarityScore(a: string, b: string): number {
  if (a === b) return 2;
  if (a.includes(b) || b.includes(a)) {
    return Math.min(a.length, b.length) / Math.max(a.length, b.length);
  }
  return 0;
}

export function autoMapFields(
  fields: ImportFieldDef[],
  csvHeaders: string[]
): FieldMapping {
  const mapping: FieldMapping = {};
  const claimed = new Set<string>();

  for (const field of fields) {
    // Match against the human label as well as the key, so a header of
    // "Type of Assessment" finds the `capacityAssessmentType` field.
    const candidates = [normalizeStr(field.key), normalizeStr(field.label)];
    let bestHeader = "";
    let bestScore = 0;

    for (const header of csvHeaders) {
      if (claimed.has(header)) continue;
      const normHeader = normalizeStr(header);
      const score = Math.max(
        ...candidates.map((c) => similarityScore(c, normHeader))
      );
      if (score > bestScore) {
        bestScore = score;
        bestHeader = header;
      }
    }

    if (bestScore > 0) {
      mapping[field.key] = bestHeader;
      claimed.add(bestHeader);
    }
  }

  return mapping;
}

export function mapRowToRecord(
  row: Record<string, string>,
  mappings: FieldMapping,
  fields: ImportFieldDef[]
) {
  return fields.reduce((record: Record<string, string | null>, field) => {
    const col = mappings[field.key];
    record[field.key] = (col ? row[col]?.trim() : null) || null;
    return record;
  }, {});
}
